import React from "react";
import { KebabVerticalIcon } from "@omega/icons";
import { PickerField as OmegaPickerField, type PickerFieldProps as OmegaPickerFieldProps } from "@omega/ui";

/**
 * Пропсы omp-фасада {@link PickerField}. \
 * Trigger chrome для внешнего picker (app window / custom surface). \
 * Overlay не владеет — только `onOpen` / `onClear`.
 */
export interface PickerFieldProps {
    className?: string;
    /** Подпись поля. */
    label?: string;
    /** Обязательное поле. */
    required?: boolean;
    /**
     * Флаг ошибки (подсветка trigger). \
     * Текст ошибки — в `FormField` / `BusinessForm.Field`.
     */
    error?: boolean;
    /** Блокировка открытия. */
    disabled?: boolean;
    /** Placeholder, если нет `displayValue` и нет `statusSlot`. */
    placeholder?: React.ReactNode;
    /** Текст / контент сводки в trigger (выбранное значение). */
    displayValue?: React.ReactNode;
    /**
     * Открывает внешний picker / overlay.
     * @param event событие активации trigger
     */
    onOpen?: OmegaPickerFieldProps["onOpen"];
    /**
     * Сброс значения; при наличии и непустом `displayValue` показывает clear.
     * @param event событие клика по кнопке очистки
     */
    onClear?: OmegaPickerFieldProps["onClear"];
    /**
     * Слот статуса вместо значения (загрузка, unresolved и т.п.). \
     * При наличии clear скрыт; контент показывается как display.
     */
    statusSlot?: React.ReactNode | null;
    /**
     * Иконка кнопки открытия. \
     * Прокидывается в `@omega/ui` `slots.openIcon`.
     * @default {@link KebabVerticalIcon} — внешний picker/window, не dropdown-chevron
     */
    openIcon?: React.ReactNode;
    /**
     * Иконка кнопки очистки. \
     * Прокидывается в `@omega/ui` `slots.clearIcon`. Без значения — package default (`Xmark`).
     */
    clearIcon?: React.ReactNode;
    /** Открыта ли связанная поверхность (a11y `aria-expanded`). */
    open?: boolean;
    /**
     * Семантика связанной поверхности.
     * @default "dialog"
     */
    popupRole?: OmegaPickerFieldProps["popupRole"];
    /** `id` связанной поверхности. */
    popupId?: string;
    /**
     * Поведение подписи.
     * @default "floating"
     */
    labelBehavior?: OmegaPickerFieldProps["labelBehavior"];
}

const defaultOpenIcon = <KebabVerticalIcon aria-hidden="true" />;

/**
 * Read-only trigger поля выбора через внешний picker (fullscreen window, карта и т.д.).
 *
 * Тонкий фасад над `@omega/ui` `PickerField`: omp-конвенции
 * (`error` → `invalid`, `fullWidth`, floating label, RU a11y labels, kebab open-icon). \
 * Не владеет overlay — только вызывает `onOpen`.
 *
 * @see {@link import("../select/Select").Select} / {@link import("../search-select/SearchSelect").SearchSelect} —
 * классический список со встроенным AdaptivePickerOverlay.
 */
export function PickerField({
    className,
    label,
    required = false,
    error = false,
    disabled = false,
    placeholder,
    displayValue,
    onOpen,
    onClear,
    statusSlot,
    openIcon = defaultOpenIcon,
    clearIcon,
    open = false,
    popupRole = "dialog",
    popupId,
    labelBehavior = "floating",
}: PickerFieldProps) {
    const hasStatus = statusSlot != null;
    const resolvedDisplay = hasStatus ? statusSlot : displayValue;
    const hasDisplayValue = resolvedDisplay != null && resolvedDisplay !== "";
    const clearable = !disabled && !hasStatus && hasDisplayValue && onClear != null;

    return (
        <OmegaPickerField
            className={className}
            label={label}
            required={required}
            invalid={error}
            disabled={disabled}
            fullWidth
            labelBehavior={labelBehavior}
            placeholder={hasStatus ? undefined : placeholder}
            displayValue={hasDisplayValue ? resolvedDisplay : undefined}
            clearable={clearable}
            clearButtonLabel="Очистить"
            openButtonLabel="Открыть выбор"
            open={open}
            openTrigger="field"
            popupRole={popupRole}
            popupId={popupId}
            onOpen={disabled ? undefined : onOpen}
            onClear={clearable ? onClear : undefined}
            slots={{
                openIcon,
                ...(clearIcon != null ? { clearIcon } : {}),
            }}
        />
    );
}
